import { appAlert } from '@/lib/appAlert';
import { formatAmountForDisplay, formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
import { Ionicons } from '@expo/vector-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { getErrorMessage } from '@/lib/functions';
import {
  createService,
  deleteService,
  fetchMyServices,
  updateService,
  type ProfessionalService,
  type ServicePriceUnit,
} from '@/lib/professionalServices';
import { ErrorText } from '@/components/ErrorText';

const UNITS: ServicePriceUnit[] = ['fixed', 'hourly', 'per_item', 'per_meter', 'per_sqm'];

export function ServicesManager({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [services, setServices] = useState<ProfessionalService[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<ProfessionalService | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priceUnit, setPriceUnit] = useState<ServicePriceUnit>('fixed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setServices(await fetchMyServices(userId));
    } catch {
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  function openNew() {
    setEditing(null);
    setTitle('');
    setDescription('');
    setPrice('');
    setPriceUnit('fixed');
    setError(null);
    setModal(true);
  }

  function openEdit(s: ProfessionalService) {
    setEditing(s);
    setTitle(s.title);
    setDescription(s.description ?? '');
    setPrice(s.price != null ? formatAmountForDisplay(s.price) : '');
    setPriceUnit(s.price_unit);
    setError(null);
    setModal(true);
  }

  async function submit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t('services.errorTitleRequired'));
      return;
    }
    const parsedPrice = price.trim() ? parseGroupedAmount(price) : null;
    if (price.trim() && (!Number.isFinite(parsedPrice) || (parsedPrice as number) < 0)) {
      setError(t('services.errorInvalidPrice'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await updateService(editing.id, {
          title: trimmedTitle,
          description: description.trim() || null,
          price: parsedPrice,
          price_unit: priceUnit,
        });
      } else {
        if (services.length >= 30) {
          setError(t('services.errorMaxReached'));
          setBusy(false);
          return;
        }
        await createService(userId, { title: trimmedTitle, description, price: parsedPrice, priceUnit });
      }
      setModal(false);
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('services.errorSaveFailed')));
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete(s: ProfessionalService) {
    appAlert(t('services.deleteTitle'), t('services.deleteBody', { title: s.title }), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteService(s.id);
            await load();
          } catch (e) {
            appAlert(t('common.error'), getErrorMessage(e, t('services.errorSaveFailed')));
          }
        },
      },
    ]);
  }

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('services.sectionTitle')}</Text>
      </View>
      <Text style={styles.hint}>{t('services.hint')}</Text>

      {loading ? (
        <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.sm }} />
      ) : (
        services.map((s) => (
          <Pressable key={s.id} style={styles.row} onPress={() => openEdit(s)} accessibilityRole="button">
            <View style={{ flex: 1 }}>
              <Text style={styles.rowTitle} numberOfLines={1}>{s.title}</Text>
              {s.price != null && (
                <Text style={styles.rowPrice}>
                  {s.price.toFixed(2)} € {s.price_unit !== 'fixed' ? t(`services.unit_${s.price_unit}`) : ''}
                </Text>
              )}
              {!s.is_active && <Text style={styles.rowInactive}>{t('services.inactive')}</Text>}
            </View>
            <Pressable onPress={() => confirmDelete(s)} hitSlop={10} style={styles.deleteBtn}>
              <Ionicons name="trash-outline" size={16} color={colors.danger} />
            </Pressable>
          </Pressable>
        ))
      )}

      <Pressable style={styles.addBtn} onPress={openNew} accessibilityRole="button">
        <Ionicons name="add" size={16} color={colors.primary} />
        <Text style={styles.addBtnText}>{t('services.action_add')}</Text>
      </Pressable>

      <Modal
        visible={modal}
        onClose={() => setModal(false)}
        editing={!!editing}
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        price={price}
        setPrice={setPrice}
        priceUnit={priceUnit}
        setPriceUnit={setPriceUnit}
        error={error}
        busy={busy}
        onSubmit={submit}
        onToggleActive={editing ? async () => {
          await updateService(editing.id, { is_active: !editing.is_active });
          setModal(false);
          await load();
        } : undefined}
        isActive={editing?.is_active ?? true}
        styles={styles}
        colors={colors}
        t={t}
      />
    </View>
  );
}

type ModalStyles = ReturnType<typeof makeStyles>;

function Modal({
  visible, onClose, editing, title, setTitle, description, setDescription, price, setPrice,
  priceUnit, setPriceUnit, error, busy, onSubmit, onToggleActive, isActive, styles, colors, t,
}: {
  visible: boolean;
  onClose: () => void;
  editing: boolean;
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  price: string;
  setPrice: (v: string) => void;
  priceUnit: ServicePriceUnit;
  setPriceUnit: (v: ServicePriceUnit) => void;
  error: string | null;
  busy: boolean;
  onSubmit: () => void;
  onToggleActive?: () => void;
  isActive: boolean;
  styles: ModalStyles;
  colors: AppColors;
  t: (k: string, o?: any) => string;
}) {
  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView
          contentContainerStyle={styles.modalScrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{editing ? t('services.editTitle') : t('services.addTitle')}</Text>
            <TextInput
              style={styles.input}
              value={title}
              onChangeText={setTitle}
              placeholder={t('services.placeholder_title')}
              placeholderTextColor={colors.textMuted}
              maxLength={100}
            />
            <TextInput
              style={[styles.input, styles.multiline]}
              value={description}
              onChangeText={setDescription}
              placeholder={t('services.placeholder_description')}
              placeholderTextColor={colors.textMuted}
              multiline
              maxLength={500}
            />
            <TextInput
              style={styles.input}
              value={price}
              onChangeText={(v) => setPrice(formatGroupedAmount(v))}
              placeholder={t('services.placeholder_price')}
              placeholderTextColor={colors.textMuted}
              keyboardType="decimal-pad"
            />
            <View style={styles.unitRow}>
              {UNITS.map((u) => (
                <Pressable
                  key={u}
                  style={[styles.unitChip, priceUnit === u && styles.unitChipActive]}
                  onPress={() => setPriceUnit(u)}
                >
                  <Text style={[styles.unitChipText, priceUnit === u && styles.unitChipTextActive]}>
                    {t(`services.unit_${u}`)}
                  </Text>
                </Pressable>
              ))}
            </View>
            <ErrorText>{error}</ErrorText>
            <Button title={t('common.save')} loading={busy} onPress={onSubmit} />
            {!!onToggleActive && (
              <Pressable onPress={onToggleActive} style={styles.toggleActiveBtn}>
                <Text style={styles.toggleActiveText}>
                  {isActive ? t('services.action_deactivate') : t('services.action_activate')}
                </Text>
              </Pressable>
            )}
            <Pressable onPress={onClose} style={styles.cancel}>
              <Text style={styles.cancelText}>{t('common.cancel')}</Text>
            </Pressable>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </RNModal>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
    },
    headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    title: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    hint: { color: colors.textMuted, fontSize: fontSize.xs, lineHeight: 16 },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 8,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    rowTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    rowPrice: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700', marginTop: 2 },
    rowInactive: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 2 },
    deleteBtn: { padding: 4 },
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderStyle: 'dashed',
      borderRadius: radius.md,
      paddingVertical: 10,
      marginTop: spacing.xs,
    },
    addBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' },
    modalScrollContent: { flexGrow: 1, justifyContent: 'center', padding: spacing.lg },
    modalCard: { backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.md },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    input: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      height: 48,
      color: colors.text,
      fontSize: fontSize.md,
    },
    multiline: { height: 80, paddingTop: spacing.sm, textAlignVertical: 'top' },
    unitRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    unitChip: {
      flexGrow: 1,
      minWidth: '30%',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 8,
      alignItems: 'center',
    },
    unitChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    unitChipText: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    unitChipTextActive: { color: '#fff' },
    error: { color: colors.danger, fontSize: fontSize.sm },
    toggleActiveBtn: { alignSelf: 'center', paddingVertical: 4 },
    toggleActiveText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    cancel: { alignSelf: 'center', paddingVertical: spacing.xs },
    cancelText: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '600' },
  });
}
